import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import Text from 'core-system/Text'
import GridCard from 'employee-platform/shared/components/GridCard'
import FlexContainer from 'core-system/FlexContainer'
import palette from 'core-system/Themes/palette'
import { Micromobility as MicromobilityIcon } from 'core-system/Icons/Sidebar'
import { GridContainer } from 'employee-platform/shared/styles/Grid'
import { AppState } from 'redux/config/store'
import Loading from 'core-system/Loading'
import { employeePlatformActions } from 'redux/employeePlatform/employeePlatformSlice'
import kingCountyService from 'redux/KingCounty/kingCountyService'
import { bikeCatalogActions } from 'redux/bikeCatalog/bikeCatalogSlice'
import { BaseProgram } from 'redux/employeePlatform/employeePlatformTypes'
import { Bike } from 'redux/bikeCatalog/bikeCatalogTypes'
import { CurrencyType } from 'features/Programs/Shared/types'
import { useGateValue } from '@statsig/react-bindings'
import KingCountyPilotWelcomeModal from 'features/KingCounty/KingCountyPilotWelcomeModal'
import MicromobilityCard from './components/MicromobilityCard'
import BikeCatalog from './components/BikeCatalog'
import NonDirectCheckoutModal from './components/NonDirectCheckoutModal'
import { isNonDirectCheckoutVendor } from './shared/components/NonDirectCheckoutHelper'

const Container = styled.div`
  padding: 2rem;
`

const Header = styled(FlexContainer)`
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
`

const HeaderIconCircle = styled.div`
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  background-color: ${palette.secondary.purple100};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const EmptyState = styled(FlexContainer)`
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  gap: 0.5rem;
`

const EmployeeMicromobilityOverviewView = React.memo(() => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const [vendorCheckoutBikeId, setVendorCheckoutBikeId] = useState<string | null>(
    null
  )
  const [vendorCheckoutError, setVendorCheckoutError] = useState<string | null>(
    null
  )
  const [nonDirectCheckoutBike, setNonDirectCheckoutBike] =
    useState<Bike | null>(null)
  const [kcPilotWelcomeDismissed, setKcPilotWelcomeDismissed] = useState(false)
  const { programs, profileData } = useSelector(
    (state: AppState) => state.employeePlatform
  )
  const allBikes = useSelector((state: AppState) => state.bikeCatalog.allBikes)
  const conversionRates = useSelector(
    (state: AppState) => state.application.properties
  )
  const { allPrograms } = programs

  const currency: CurrencyType =
    (profileData?.worksiteCurrency as CurrencyType) || 'USD'

  const micromobilityPrograms: BaseProgram[] = allPrograms?.MICROMOBILITY
    ? Object.values(allPrograms.MICROMOBILITY).filter(
        (p) => p.status === 'ACTIVE'
      )
    : []
  const hasBikeCatalogFeature = useGateValue('bike_catalog')
  const showKcWelcomeMessage = useGateValue('show_kc_welcome_message')
  const isFirstTimeFromOnboarding = Boolean(
    (location.state as { firstTime?: boolean } | null)?.firstTime
  )
  const showKcPilotWelcomeModal =
    isFirstTimeFromOnboarding &&
    showKcWelcomeMessage &&
    !kcPilotWelcomeDismissed

  const handleCloseKcPilotWelcomeModal = useCallback(() => {
    setKcPilotWelcomeDismissed(true)
    navigate(location.pathname, { replace: true, state: null })
    dispatch(employeePlatformActions.getAllPrograms())
  }, [dispatch, location.pathname, navigate])

  useEffect(() => {
    if (allPrograms === null) {
      dispatch(employeePlatformActions.getAllPrograms())
    }
  }, [dispatch, allPrograms])

  useEffect(() => {
    if (hasBikeCatalogFeature) {
      dispatch(bikeCatalogActions.getAllBikes())
    }
  }, [dispatch, hasBikeCatalogFeature])

  const continueToVendorCheckout = useCallback(async (bike: Bike) => {
    if (!bike.productUrl) {
      return
    }

    setVendorCheckoutBikeId(bike.id)
    setVendorCheckoutError(null)

    try {
      await kingCountyService.selectBikeForCheckout(bike.id)
      window.open(bike.productUrl, '_blank', 'noopener,noreferrer')
    } catch {
      setVendorCheckoutError(
        'Unable to save your bike selection. Please try again.'
      )
    } finally {
      setVendorCheckoutBikeId(null)
    }
  }, [])

  const handleVendorCheckout = useCallback(
    async (bike: Bike) => {
      if (isNonDirectCheckoutVendor(bike.vendor)) {
        setNonDirectCheckoutBike(bike)
        return
      }

      await continueToVendorCheckout(bike)
    },
    [continueToVendorCheckout]
  )

  const handleContinueToNonDirectCheckout = useCallback(async () => {
    if (!nonDirectCheckoutBike) {
      return
    }

    await continueToVendorCheckout(nonDirectCheckoutBike)
    setNonDirectCheckoutBike(null)
  }, [continueToVendorCheckout, nonDirectCheckoutBike])

  if (allPrograms === null) {
    return <Loading fullPage />
  }

  return (
    <Container>
      <Header>
        <FlexContainer alignItems='center' style={{ gap: '0.75rem' }}>
          <HeaderIconCircle>
            <MicromobilityIcon
              width={18}
              height={18}
              color={palette.secondary.purple700}
            />
          </HeaderIconCircle>
          <Text variant='h2'>Micromobility</Text>
        </FlexContainer>
        <Text variant='action3' textColor={palette.text.secondary}>
          {micromobilityPrograms.length} program
          {micromobilityPrograms.length === 1 ? '' : 's'}
        </Text>
      </Header>

      {micromobilityPrograms.length === 0 ? (
        <GridCard headerTitle='Micromobility Programs'>
          <EmptyState>
            <Text variant='h4' textColor={palette.text.secondary}>
              No active Micromobility program
            </Text>
            <Text variant='action3' textColor={palette.text.secondary}>
              You do not have access to a Micromobility program at this time.
            </Text>
          </EmptyState>
        </GridCard>
      ) : (
        <GridContainer numColumns={2} customGridGap='1.5rem'>
          {micromobilityPrograms.map((program) => {
            return (
              <MicromobilityCard
                key={program.id}
                program={program}
                currency={currency}
                conversionRates={conversionRates}
              />
            )
          })}
        </GridContainer>
      )}

      {hasBikeCatalogFeature && micromobilityPrograms.length > 0 && (
        <BikeCatalog
          bikes={allBikes}
          subsidyBudgetCents={micromobilityPrograms[0].budget ?? 0}
          currency={currency}
          conversionRates={conversionRates}
          vendorCheckoutBikeId={vendorCheckoutBikeId}
          vendorCheckoutError={vendorCheckoutError}
          onVendorCheckout={handleVendorCheckout}
        />
      )}
      <NonDirectCheckoutModal
        open={Boolean(nonDirectCheckoutBike)}
        bike={nonDirectCheckoutBike}
        isLoading={
          Boolean(nonDirectCheckoutBike) &&
          vendorCheckoutBikeId === nonDirectCheckoutBike?.id
        }
        onClose={() => setNonDirectCheckoutBike(null)}
        onContinue={handleContinueToNonDirectCheckout}
      />
      <KingCountyPilotWelcomeModal
        open={showKcPilotWelcomeModal}
        onClose={handleCloseKcPilotWelcomeModal}
      />
    </Container>
  )
})

if (process.env.NODE_ENV !== 'production') {
  EmployeeMicromobilityOverviewView.displayName =
    'EmployeeMicromobilityOverviewView'
}

export default EmployeeMicromobilityOverviewView
